import { defineNuxtRouteMiddleware } from '#app'
import type { RouteLocationNormalized } from 'vue-router'

// Define public routes that don't require authentication (without locale prefix)
const PUBLIC_ROUTES = [
  '/',
  '/about',
  '/contact',
  '/locations',
  '/pricing',
  '/how-to-lend',
  '/how-to-use-app',
  '/install-lock',
  '/troubleshooting',
  '/privacy',
  '/terms',
  '/auth/login',
  '/auth/register',
  '/auth/forgot-password',
  '/auth/reset-password',
  '/auth/verify-otp'
]

// Public route prefixes — anything starting with one of these is public.
// Used for dynamic sub-routes like `/locations/<slug>` that we don't want
// to enumerate one-by-one.
const PUBLIC_PREFIXES = [
  '/locations/'
]

const LOCALE_PREFIX = 'ja'

/** Match paths like `/ja/about` against lists that use default-locale paths */
function pathWithoutLocale(path: string): string {
  const prefixed = path.match(new RegExp(`^\\/${LOCALE_PREFIX}(?=\\/|$)`))
  if (prefixed) {
    const rest = path.slice(prefixed[0].length) || '/'
    return rest.startsWith('/') ? rest : `/${rest}`
  }
  return path
}

/** Build a locale-aware path from the navigation target (prefix_except_default). */
function localePathForRoute(to: RouteLocationNormalized, path: string): string {
  const normalized = path.startsWith('/') ? path : `/${path}`
  const hasLocalePrefix = new RegExp(`^\\/${LOCALE_PREFIX}(?=\\/|$)`).test(to.path)
  return hasLocalePrefix ? `/${LOCALE_PREFIX}${normalized}` : normalized
}

// Define auth-only routes (logged in users can't access these)
const AUTH_ONLY_ROUTES = [
  '/auth/login',
  '/auth/register',
  '/auth/verify-otp'
]

export default defineNuxtRouteMiddleware(async (to) => {
  const routePath = pathWithoutLocale(to.path)
  const isPublic =
    PUBLIC_ROUTES.includes(routePath) ||
    PUBLIC_PREFIXES.some((prefix) => routePath.startsWith(prefix))
  const isAuthOnly = AUTH_ONLY_ROUTES.includes(routePath)
  
  console.log('🛡️ Auth middleware starting:', {
    path: to.path,
    isPublic,
    isAuthOnly
  })

  // Skip initialization on server-side
  if (process.server) {
    console.log('⏭️ Skipping auth check on server-side')
    return
  }

  try {
    const auth = resolveAuthStore()
    if (!auth) {
      console.log('⚠️ Pinia not ready yet, skipping auth check')
      return
    }

    console.log('🛡️ Auth middleware starting:', {
      path: to.path,
      isPublic,
      isAuthOnly,
      currentToken: !!auth.token
    })

    // Initialize auth store if needed
    if (!auth.token) {
      console.log('🔄 No token in store, initializing...')
      await auth.init()
    }

    const hasToken = !!auth.token
    let isAuthenticated = false

    if (hasToken) {
      // Check token expiration
      const tokenData = auth.tokenData
      if (tokenData?.exp) {
        const now = Math.floor(Date.now() / 1000)
        const timeUntilExpiry = tokenData.exp - now

        if (timeUntilExpiry <= 0) {
          console.log('⌛ Token expired')
          auth.logout()
        } else {
          // Refresh token if it expires soon
          if (timeUntilExpiry < 300) {
            console.log('🔄 Refreshing token')
            await auth.refreshToken()
          }
          isAuthenticated = await auth.checkAuth()
        }
      }
    }

    console.log('🚦 Auth status:', {
      path: to.path,
      isPublic,
      isAuthOnly,
      hasToken,
      isAuthenticated
    })

    // Handle auth-only routes (like login page)
    if (isAuthOnly && isAuthenticated) {
      console.log('👤 Already authenticated, redirecting to dashboard')
      return navigateTo(localePathForRoute(to, auth.dashboardPath))
    }

    // Handle protected routes
    if (!isPublic && !isAuthenticated) {
      console.log('⚠️ Protected route access denied, redirecting to login')
      return navigateTo(localePathForRoute(to, '/auth/login'))
    }
  } catch (error) {
    console.error('❌ Auth middleware error:', error)
    try {
      const auth = resolveAuthStore()
      auth?.logout()
      return navigateTo(localePathForRoute(to, '/auth/login'))
    } catch {
      return navigateTo(localePathForRoute(to, '/auth/login'))
    }
  }
}) 